import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Lightbulb,
  LogOut,
  User,
  ArrowLeft,
  Users,
  Shield,
  TrendingUp,
  MessageCircle,
  Search,
  Eye,
  CheckCircle2,
  Clock,
  Archive
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [dashboardStats, setDashboardStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [userIdeas, setUserIdeas] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('overview'); // 'overview' or 'user-detail'

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`),
        axios.get(`${API}/admin/users`)
      ]);

      setDashboardStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const [statsRes, ideasRes] = await Promise.all([
        axios.get(`${API}/admin/users/${userId}/stats`),
        axios.get(`${API}/admin/users/${userId}/ideas`)
      ]);

      setUserStats(statsRes.data);
      setUserIdeas(ideasRes.data);
      setSelectedUser(users.find(u => u.id === userId));
      setViewMode('user-detail');
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] dark:bg-zinc-900">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b border-zinc-200 dark:border-zinc-700 bg-white/80 dark:bg-zinc-800/80 backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:supports-[backdrop-filter]:bg-zinc-800/60">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => viewMode === 'user-detail' ? setViewMode('overview') : navigate('/dashboard')}
              data-testid="back-button"
              className="text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-white rounded-lg p-2 transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-[#5B6B8F] flex items-center justify-center">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-zinc-900 dark:text-white">Admin Dashboard</span>
                {viewMode === 'user-detail' && selectedUser && (
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">{selectedUser.name}</p>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-[#E8F5E9] dark:bg-[#1B5E20]/30 rounded-lg border border-[#A5D6A7] dark:border-[#2E7D32]">
              <Shield size={16} className="text-[#5B6B8F] dark:text-[#8BC34A]" />
              <span className="text-sm font-medium text-[#1B5E20] dark:text-[#8BC34A]">{user.name} (Admin)</span>
            </div>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-white rounded-lg p-2 transition-colors"
              title="Logout"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12 text-zinc-500 dark:text-zinc-400">Loading...</div>
        ) : viewMode === 'overview' ? (
          <>
            {/* System Stats */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-zinc-900 dark:text-white mb-4">System Overview</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm" data-testid="stat-total-users">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide font-mono">Total Users</span>
                    <Users size={16} className="text-blue-500" />
                  </div>
                  <p className="text-2xl font-bold text-zinc-900 dark:text-white">{dashboardStats?.total_users || 0}</p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-mono">
                    {dashboardStats?.admin_users || 0} admins, {dashboardStats?.regular_users || 0} users
                  </p>
                </div>

                <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide font-mono">Total Ideas</span>
                    <Lightbulb size={16} className="text-[#689F38]" />
                  </div>
                  <p className="text-2xl font-bold text-zinc-900 dark:text-white">{dashboardStats?.total_ideas || 0}</p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-mono">{dashboardStats?.active_ideas || 0} active</p>
                </div>

                <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide font-mono">In Progress</span>
                    <Clock size={16} className="text-blue-500" />
                  </div>
                  <p className="text-2xl font-bold text-zinc-900 dark:text-white">{dashboardStats?.in_progress || 0}</p>
                </div>

                <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide font-mono">Completed</span>
                    <CheckCircle2 size={16} className="text-green-500" />
                  </div>
                  <p className="text-2xl font-bold text-zinc-900 dark:text-white">{dashboardStats?.completed || 0}</p>
                </div>

                <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide font-mono">Comments</span>
                    <MessageCircle size={16} className="text-purple-500" />
                  </div>
                  <p className="text-2xl font-bold text-zinc-900 dark:text-white">{dashboardStats?.total_comments || 0}</p>
                </div>
              </div>
            </div>

            {/* Most Active Users */}
            {dashboardStats?.most_active_users?.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-bold text-zinc-900 dark:text-white mb-4">Most Active Users</h3>
                <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm">
                  <div className="space-y-3">
                    {dashboardStats.most_active_users.map((userStat, index) => (
                      <div key={userStat.user_id} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-bold text-zinc-400 dark:text-zinc-600 font-mono">#{index + 1}</span>
                          <span className="font-medium text-zinc-900 dark:text-white">{userStat.user_name}</span>
                        </div>
                        <span className="text-sm text-zinc-600 dark:text-zinc-400 font-mono">{userStat.idea_count} ideas</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Users List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-zinc-900 dark:text-white">All Users</h3>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-500" size={18} />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="search-users-input"
                    className="w-full h-10 pl-10 pr-4 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-sm text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#689F38] focus-visible:border-transparent"
                  />
                </div>
              </div>

              <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-zinc-50 dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">Ideas</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">Completed</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">Assigned</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">Comments</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">Joined</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
                      {filteredUsers.map((u) => (
                        <tr key={u.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-900/50" data-testid={`user-row-${u.id}`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="font-medium text-zinc-900 dark:text-white">{u.name}</div>
                              <div className="text-sm text-zinc-500 dark:text-zinc-400">{u.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {u.role === 'admin' ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#C8E6C9] dark:bg-[#1B5E20]/30 text-[#1B5E20] dark:text-[#8BC34A] border border-[#A5D6A7] dark:border-[#2E7D32]">
                                Admin
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-zinc-100 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-600">
                                User
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900 dark:text-white font-mono">{u.total_ideas || 0}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900 dark:text-white font-mono">{u.completed_ideas || 0}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900 dark:text-white font-mono">{u.assigned_ideas || 0}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900 dark:text-white font-mono">{u.comments_count || 0}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-500 dark:text-zinc-400 font-mono">
                            {format(new Date(u.created_at), 'MMM d, yyyy')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => viewUserDetails(u.id)}
                              data-testid={`view-user-${u.id}`}
                              className="flex items-center gap-1.5 text-sm text-[#5B6B8F] dark:text-[#8BC34A] hover:text-[#4A5A7C] dark:hover:text-[#5B6B8F]-300 font-medium"
                            >
                              <Eye size={16} />
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        ) : (
          /* User Detail View */
          <>
            {userStats && (
              <>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-zinc-900 dark:text-white mb-4">{userStats.user.name}'s Statistics</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide font-mono">Total Ideas</span>
                        <Lightbulb size={16} className="text-[#689F38]" />
                      </div>
                      <p className="text-2xl font-bold text-zinc-900 dark:text-white">{userStats.total_ideas}</p>
                    </div>

                    <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide font-mono">Completed</span>
                        <CheckCircle2 size={16} className="text-green-500" />
                      </div>
                      <p className="text-2xl font-bold text-zinc-900 dark:text-white">{userStats.completed}</p>
                    </div>

                    <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide font-mono">In Progress</span>
                        <Clock size={16} className="text-blue-500" />
                      </div>
                      <p className="text-2xl font-bold text-zinc-900 dark:text-white">{userStats.in_progress}</p>
                    </div>

                    <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide font-mono">Comments</span>
                        <MessageCircle size={16} className="text-purple-500" />
                      </div>
                      <p className="text-2xl font-bold text-zinc-900 dark:text-white">{userStats.comments_made}</p>
                    </div>
                  </div>
                </div>

                {/* User's Ideas */}
                <div>
                  <h3 className="text-lg font-bold text-zinc-900 dark:text-white mb-4">Ideas ({userIdeas.length})</h3>
                  <div className="space-y-4">
                    {userIdeas.map((idea) => (
                      <div
                        key={idea.id}
                        className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm"
                      >
                        <h4 className="text-lg font-semibold text-zinc-900 dark:text-white mb-2">{idea.title}</h4>
                        {idea.description && (
                          <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-3">{idea.description}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-zinc-100 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-600 font-mono">
                            {idea.category}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                            idea.progress_status === 'Completed'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-900 dark:text-green-400 border-green-200 dark:border-green-800'
                              : idea.progress_status === 'In Progress'
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-400 border-blue-200 dark:border-blue-800'
                              : 'bg-zinc-100 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-300 border-zinc-200 dark:border-zinc-600'
                          }`}>
                            {idea.progress_status}
                          </span>
                          <span className="text-xs text-zinc-500 dark:text-zinc-400 font-mono">
                            {format(new Date(idea.created_at), 'MMM d, yyyy')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
